/**
 * Store Portal API Service
 * Handles all API calls for the retail store inventory management system.
 */

// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\.+\/*$/, '').replace(/\/+$/, '');

// Helper for API calls with cookie authentication
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include', // Send cookies with requests
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  // Handle 401 — token expired, redirect to login
  // Skip for auth-check endpoints (401 means "not logged in" which is normal)
  if (response.status === 401) {
    if (endpoint.includes('/me/') || endpoint.includes('/my-locations/') || endpoint.includes('/token/')) {
      const error = await response.json().catch(() => ({ detail: 'Session expired' }));
      throw new Error(error.detail || 'Session expired');
    }
    localStorage.removeItem('rbf_user_data');
    localStorage.removeItem('user');
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/store/login';
    }
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    // Preserve the structured body + status so callers can act on it (e.g. the
    // POS reservation/oversell guard 409 carries {code, product_id, requested,
    // available, requires_override}). err.message stays unchanged.
    const e: any = new Error(error.detail || error.error || 'Request failed');
    e.status = response.status;
    e.data = error;
    throw e;
  }

  return response.json();
};

// Auth API - Store uses shared team auth, so /api/auth/me/ endpoint
export const storeAuthAPI = {
  login: async (username: string, password: string) => {
    // Store uses shared team login - this redirects to /login
    // But keep endpoint for backwards compatibility
    const response = await fetch(`${API_BASE_URL}/token/`, {
      method: 'POST',
      credentials: 'include', // Important: send cookies
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: username, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(error.detail || 'Login failed');
    }

    return response.json();
  },

  logout: async () => {
    try {
      await apiCall('/auth/logout/', { method: 'POST' });
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('rbf_user_data');
    localStorage.removeItem('user');
  },

  me: async () => {
    // Use main team auth endpoint since store uses shared login
    return apiCall('/auth/me/');
  },
};

// Dashboard API
export const storeDashboardAPI = {
  getDashboard: () => apiCall('/store/dashboard/'),
};

// Inventory API
export const storeInventoryAPI = {
  getAll: () => apiCall('/store/inventory/'),

  getOne: (id: number) => apiCall(`/store/inventory/${id}/`),

  create: (data: {
    product_id: number;
    quantity?: number;
    location?: string;
    reorder_threshold?: number;
    reorder_quantity?: number;
    notes?: string;
  }) => apiCall('/store/inventory/create/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id: number, data: {
    reorder_threshold?: number;
    reorder_quantity?: number;
    location?: string;
    notes?: string;
  }) => apiCall(`/store/inventory/${id}/update/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  adjust: (id: number, adjustment: number, type: string = 'COUNT_ADJUST', notes: string = '') =>
    apiCall(`/store/inventory/${id}/adjust/`, {
      method: 'POST',
      body: JSON.stringify({ adjustment, type, notes }),
    }),

  count: (id: number, quantity: number, notes: string = '') =>
    apiCall(`/store/inventory/${id}/count/`, {
      method: 'POST',
      body: JSON.stringify({ quantity, notes }),
    }),

  // weight_tracked pound pool: a signed pounds delta (shrink/receiving/correction).
  adjustLbs: (id: number, adjustmentLbs: number, type: string = 'COUNT_ADJUST', notes: string = '') =>
    apiCall(`/store/inventory/${id}/adjust/`, {
      method: 'POST',
      body: JSON.stringify({ adjustment_lbs: adjustmentLbs, type, notes }),
    }),

  // weight_tracked pound pool: a physical count that SETS the pool to countedLbs.
  countLbs: (id: number, countedLbs: number, notes: string = '') =>
    apiCall(`/store/inventory/${id}/count/`, {
      method: 'POST',
      body: JSON.stringify({ quantity_lbs: countedLbs, notes }),
    }),

  getByProduct: (productId: number) =>
    apiCall(`/store/inventory/by-product/${productId}/`),
};

// Bulk Bin API
export const bulkBinAPI = {
  getAll: () => apiCall('/store/bulk-bins/'),

  getOne: (id: number) => apiCall(`/store/bulk-bins/${id}/`),

  getByQR: (qrCode: string) => apiCall(`/store/bulk-bins/qr/${qrCode}/`),

  create: (data: {
    name: string;
    product_id?: number;
    feed_form?: string;
    barcode?: string;
    capacity_lbs?: number;
    current_lbs?: number;
    reorder_threshold_percent?: number;
    reorder_quantity_lbs?: number;
    notes?: string;
  }) => apiCall('/store/bulk-bins/create/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  fill: (id: number, amountLbs: number, notes: string = '') =>
    apiCall(`/store/bulk-bins/${id}/fill/`, {
      method: 'POST',
      body: JSON.stringify({ amount_lbs: amountLbs, notes }),
    }),

  adjust: (id: number, levelLbs: number, notes: string = '') =>
    apiCall(`/store/bulk-bins/${id}/adjust/`, {
      method: 'POST',
      body: JSON.stringify({ level_lbs: levelLbs, notes }),
    }),

  update: (id: number, data: {
    name?: string;
    product_id?: number | null;
    feed_form?: string;
    capacity_lbs?: number;
    current_lbs?: number;
    reorder_threshold_percent?: number;
    reorder_quantity_lbs?: number;
    is_active?: boolean;
    notes?: string;
  }) => apiCall(`/store/bulk-bins/${id}/update/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// FIVE_STAR_FEEDS removed 2026-05-16.
//
// Previously a hardcoded fallback list for the bulk-bins UI (drove
// EditBinModal / AddBinModal dropdowns when the formulas API errored,
// and drove the StoreBulkBins card primary-label parser). Both uses
// caused real bugs:
//   * Label parser: substring match misfired ("Goat Chow" inside
//     "Dairy Goat Chow Mash" → wrong label). The card now reads
//     bin.product_name directly.
//   * Modal fallback: showing stale hardcoded options when the live
//     formula API fails silently substituted bad data; the modal now
//     surfaces the load failure with an empty dropdown instead.
//
// If you find yourself wanting to re-add a hardcoded list of formula
// names, check first whether the live /api/feed-formulas/?mill_only=true
// endpoint can serve the use case — that's the source of truth.

export const FEED_FORMS = [
  { value: 'MASH', label: 'Mash' },
  { value: 'PELLET', label: 'Pellet' },
  { value: 'WHOLE', label: 'Whole Grain' },
];

// Alerts API
export const storeAlertsAPI = {
  getAll: (showResolved: boolean = false) =>
    apiCall(`/store/alerts/?show_resolved=${showResolved}`),

  markRead: (id: number) => apiCall(`/store/alerts/${id}/read/`, { method: 'POST' }),

  resolve: (id: number) => apiCall(`/store/alerts/${id}/resolve/`, { method: 'POST' }),
};

// Transactions API
export const storeTransactionsAPI = {
  getAll: (limit: number = 50, productId?: number) => {
    let url = `/store/transactions/?limit=${limit}`;
    if (productId) url += `&product_id=${productId}`;
    return apiCall(url);
  },
};

// Auto-Reorder Rules API
export const autoReorderAPI = {
  getAll: () => apiCall('/store/auto-reorder/'),

  create: (data: {
    product_id: number;
    trigger_level: number;
    reorder_quantity: number;
    is_bulk_bin?: boolean;
    trigger_level_lbs?: number;
    reorder_quantity_lbs?: number;
    cooldown_hours?: number;
  }) => apiCall('/store/auto-reorder/create/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  delete: (id: number) => apiCall(`/store/auto-reorder/${id}/delete/`, { method: 'DELETE' }),
};

// Products API
export const storeProductsAPI = {
  getAll: () => apiCall('/store/products/'),

  linkBarcode: (productId: number, barcode: string) =>
    apiCall(`/store/products/${productId}/link-barcode/`, {
      method: 'POST',
      body: JSON.stringify({ barcode }),
    }),
};

// Restock Request API
export const storeRestockAPI = {
  getLowStockItems: () => apiCall('/store/low-stock-items/'),

  createRestockRequest: (items: Array<{ inventory_id: number; quantity: number }>, notes?: string) =>
    apiCall('/store/restock-request/', {
      method: 'POST',
      body: JSON.stringify({ items, notes }),
    }),
};

// Partner API Integration (for POS systems)
export const storePartnerAPI = {
  // API Key Management
  getAPIKeys: () => apiCall('/store/api-keys/'),

  createAPIKey: (data: { name: string; key_type: 'live' | 'test'; permissions?: Record<string, boolean> }) =>
    apiCall('/store/api-keys/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  revokeAPIKey: (keyId: number) =>
    apiCall(`/store/api-keys/${keyId}/`, {
      method: 'DELETE',
    }),

  // Webhook Configuration
  getWebhooks: () => apiCall('/store/webhooks/'),

  createWebhook: (data: { webhook_url: string; events: string[] }) =>
    apiCall('/store/webhooks/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteWebhook: (webhookId: number) =>
    apiCall(`/store/webhooks/${webhookId}/`, {
      method: 'DELETE',
    }),

  // Purchase Sync Log
  getPurchaseSyncLog: (limit: number = 50) =>
    apiCall(`/store/purchase-sync-log/?limit=${limit}`),
};

// ================================================
// POS (Point of Sale) API
// ================================================

export interface POSProduct {
  id: number;
  name: string;
  sku: string;
  barcode: string;
  upc: string;
  retail_price: string;
  wholesale_price: string;
  effective_price: string;
  has_formula: boolean;
  base_price: string | null;
  pos_price_override: string | null;
  size_lbs: string;
  is_byoc: boolean;
  is_variable_price: boolean;
  // Non-feed pool-of-pounds product: prompt for the package tag weight, fixed price.
  weight_tracked?: boolean;
  is_taxable: boolean;
  tax_rate: string;
  tax_exempt_eligible: boolean;
  category_name: string;
  product_category: number | null;
  quick_button: boolean;
  display_order: number;
  is_active: boolean;
  supplier: string;
}

export interface POSCategory {
  id: number;
  name: string;
  slug: string;
  description: string;
  display_order: number;
  color: string;
  icon: string;
  product_count: number;
}

// One read-only order line on an invoice ("what feed is this invoice
// for") — mirrors backend pos_views._invoice_line_items. Display-only
// context: never affects the amount collected.
export interface POSInvoiceLineItem {
  name: string;
  quantity_lbs: number;
  packages: number;
  line_total: number;
}

// Invoice-payment context carried on INVPAY cart/receipt lines —
// mirrors backend pos_views._invoice_payment_context.
export interface POSInvoicePaymentContext {
  invoice_number: string;
  applied: number;
  remaining_balance: number | null;
  paid_in_full: boolean | null;
  line_items: POSInvoiceLineItem[];
}

// Shape of one open invoice from GET /pos/open-invoices/ — mirrors
// backend customer_ar.serialize_invoice (+ line_items added by the POS
// endpoint).
export interface POSOpenInvoice {
  id: number;
  invoice_number: string;
  invoice_date: string | null;
  due_date: string | null;
  total_amount: string;
  balance_due: string;
  status: string;
  is_overdue: boolean;
  order_number: string | null;
  line_items: POSInvoiceLineItem[];
}

export interface POSCustomer {
  id: number | null;
  name: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  member_code: string;
  barcode_id: string;
  member_since: string | null;
  local_access?: boolean;
  // Set on UnmatchedVisitor shadow profiles (walk-in customers built
  // from Helcim receipts who haven't joined Bridge Club yet). Real
  // BridgeClubMember rows have is_unmatched=false.
  is_unmatched?: boolean;
  unmatched_visitor_id?: number;
  visit_count?: number;
  total_spend?: string;
  last_visit?: string | null;
  card_last_four?: string;
  card_type?: string;
  // Profile picture URL — null if member hasn't uploaded one.
  profile_picture?: string | null;
  // Local Bridge Club purchase points (only populated when local_access=true).
  local_points?: {
    balance: number;
    redeemable_rewards: number;
    points_to_next_reward: number;
    lifetime_earned: number;
    lifetime_redeemed: number;
  } | null;
  // Tax-exemption certificate status for the till banner (Build B).
  // DISPLAY ONLY — the backend never sets exemption from this; the cashier
  // decides. Null when the customer has no certificate (or isn't local).
  exempt_certificate?: {
    has_valid_certificate: boolean;
    certificate_number: string;
    expiration_date: string;
    days_until_expiration: number;
    // 'valid' | 'expired' | 'expiring_soon' | 'inactive'
    status: string;
  } | null;
  // Set on wholesale-Customer rows surfaced via the third tier of
  // /pos/customers/. These rows have a populated `pending_orders` list
  // (Will-Call mill-pickup orders waiting to be loaded into the POS).
  is_wholesale?: boolean;
  contact_person?: string;
  pending_orders?: Array<{
    id: number;
    order_number: string;
    placed_at: string | null;
    total: number;
    item_count: number;
    items_preview: string;
    status: string;
    // True when SalesOrder.status is below READY_TO_SHIP — UI shows
    // an amber "may not be physically ready yet" warning.
    status_warning: boolean;
    shipping_method: string;
    shipping_method_display: string;
    is_tax_exempt: boolean;
  }>;
}

export interface POSFeedBin {
  id: number;
  name: string;
  feed_category: string;
  feed_category_display: string;
  current_lbs: number;
  fill_percentage: number;
  status: string;
  days_remaining: number | null;
  feed_product_name: string | null;
  form_type: string | null;
  form_type_display: string | null;
}

export interface POSCustomerDetail {
  member: POSCustomer;
  bins: POSFeedBin[];
}

export interface POSTransactionItem {
  id: number;
  // Null for wholesale lines (display info lives on name_snapshot/sku_snapshot).
  product: number | null;
  product_name: string;  // uses backend display_name (falls through to snapshot)
  product_sku: string;
  quantity: string;
  is_weight_based: boolean;
  // Exact typed tag weight for a weight_tracked unit line (lbs). Null otherwise.
  sold_weight_lbs?: string | null;
  unit_price: string;
  line_total: string;
  tax_rate: string;
  tax_amount: string;
  discount_type: string;
  discount_value: string;
  discount_amount: string;
  discount_reason: string;
  is_tax_exempt: boolean;
  tax_exempt_eligible: boolean;
  bulk_bin: number | null;
  // Set when this line came from a wholesale SalesOrderItem. UI uses
  // this to render the lock affordance + "Wholesale" badge.
  source_wholesale_line_id?: number | null;
  is_wholesale_line?: boolean;
  name_snapshot?: string;
  sku_snapshot?: string;
  // Non-null only on INVPAY (invoice-payment) lines: invoice number,
  // applied amount, balance state + the invoice's read-only line items.
  invoice_payment?: POSInvoicePaymentContext | null;
  // Bag-credit / donation-fulfillment (Phase 3): a $0 pull against a member's
  // bag grant. bag_credit_info powers the "applied from bag credit" display.
  is_donation?: boolean;
  bag_grant?: number | null;
  donation_cost?: string | null;
  bag_pull_declined?: boolean;
  // "Applied from bag credit (N of M remaining)" — the SAME string the receipt
  // prints (one backend source), shown persistently on the cart line.
  bag_credit_label?: string | null;
  bag_credit_info?: {
    grant_id: number;
    granted: number;
    remaining_after: number;
    pulled_here: number;
  } | null;
  // Pay-down-tab line (Phase 4): product-less, tax-exempt, collected by real tender.
  is_tab_payment?: boolean;
}

export interface TabSnapshot {
  customer_id: number;
  balance: string;
  limit: string | null;
  available: string | null;
  is_default_limit: boolean;
}

export interface POSPayment {
  id: number;
  payment_method: string;
  amount: string;
  status: string;
  helcim_transaction_id: string;
  card_type: string;
  card_last_four: string;
  is_credit: boolean;
  amount_tendered: string;
  change_given: string;
  check_number: string;
  created_at: string;
  processed_at: string | null;
}

export interface POSTransaction {
  id: number;
  transaction_number: string;
  session: number;
  cashier: number;
  cashier_name: string;
  bridge_club_member: number | null;
  customer: number | null;
  customer_name: string | null;
  started_at: string;
  completed_at: string | null;
  subtotal: string;
  tax_amount: string;
  discount_amount: string;
  surcharge_amount: string;
  total: string;
  // Local Bridge Club points redeemed on this sale. local_points_redeemed_amount
  // is the dollar credit that reduces `total` (rendered as a "Reward credit" line).
  local_points_redeemed?: number;
  local_points_redeemed_amount?: string;
  status: string;
  discount_reason: string;
  cart_discount_type: string;
  cart_discount_value: string;
  tax_exempt: boolean;
  void_reason: string;
  notes: string;
  items: POSTransactionItem[];
  payments: POSPayment[];
  // Populated when this txn was created by loading a wholesale Will-Call
  // SalesOrder. Cart UI shows a "Loaded from Wholesale Order #X" banner
  // and locks the wholesale lines (only walk-in add-ons editable).
  source_wholesale_order_id?: number | null;
  source_wholesale_order_number?: string | null;
  source_wholesale_order_status?: string | null;
  attached_customer_id?: number | null;
  attached_customer_name?: string | null;
  // Customer tab snapshot (Phase 4) — null when no customer/tab.
  tab?: TabSnapshot | null;
  // Set when the cashier parked this cart (persistent multi-cart, Phase 6).
  parked_at?: string | null;
}

export interface POSSession {
  id: number;
  cashier: number;
  cashier_name: string;
  terminal_id: string;
  register: number | null;
  register_name: string | null;
  opened_at: string;
  closed_at: string | null;
  status: string;
  opening_cash: string;
  closing_cash: string;
  expected_cash: string;
  cash_variance: string;
  opening_check_float: string;
  closing_check_count: string | null;
  expected_checks: string | null;
  check_variance: string | null;
  transaction_count: number;
  total_sales: number;
  notes: string;
}

export interface POSRegisterInfo {
  id: number;
  name: string;
  location: string;
}

export interface POSReceipt {
  store_name: string;
  store_address: string;
  store_phone: string;
  transaction_number: string;
  date: string;
  time: string;
  cashier: string;
  items: Array<{
    name: string;
    quantity: number;
    unit: string;
    unit_price: number;
    total: number;
    tax: number;
    is_tax_exempt?: boolean;
    // weight_tracked line: exact tag weight for "Name — 0.58 lb" display.
    sold_weight_lbs?: number | null;
    is_donation?: boolean;
    bag_credit_label?: string;
    discount_type?: string;
    discount_value?: number;
    discount_amount?: number;
    original_total?: number;
    // Present on A1 invoice-payment (INVPAY) lines: the receipt prints
    // the invoice number, post-payment balance, and the invoice's line
    // items instead of the generic (truncating) name/qty lines.
    invoice_payment?: POSInvoicePaymentContext;
  }>;
  subtotal: number;
  tax: number;
  discount: number;
  surcharge: number;
  total: number;
  tax_exempt?: boolean;
  cart_discount_type?: string;
  cart_discount_value?: number;
  payments: Array<{
    method: string;
    amount: number;
    card_type?: string;
    card_last_four?: string;
    is_credit?: boolean | null;
    check_number?: string | null;
    change?: number | null;
  }>;
  customer_name: string | null;
}

// Receipt for a paid-online BridgeClubOrder that never went through the till.
// Served by GET /store/orders/<id>/receipt/ (store_views.store_order_receipt).
// Deliberately narrower than POSReceipt: a BC order has no transaction number,
// no cashier, no session, no per-line tax and no payments[] array — see the
// thermal renderer's buildBCOrderReceiptData for how each gap is handled.
export interface BCOrderReceipt {
  order_number: string;
  paid_at: string | null;
  date: string;
  time: string;
  customer_name: string;
  pickup_method: 'in_store' | 'locker';
  fulfillment_method: string;
  // Header wording, resolved SERVER-side (_bc_fulfillment) so it can never
  // drift from the orders-history row. Never re-derive this client-side.
  fulfillment_label: string;
  items: Array<{
    name: string;
    quantity: number;
    unit_price: number;
    line_total: number;
  }>;
  subtotal: number;
  convenience_fee: number;
  tax: number;
  total: number;
  helcim_transaction_id: string;
  card_last_four: string;
  card_type: string;
  payment_method: string;
  // ── Substitution (Build 3c) ──────────────────────────────────────
  // `total` above is always what the customer ACTUALLY paid;
  // charged_total is what was taken at checkout. The gap is explained by
  // exactly one of refunded_cash / collected_cash / waived_amount.
  substitutions: Array<{
    original_name: string;
    original_quantity: number;
    original_line_total: number;
    substitute_name: string;
    substitute_quantity: number;
    substitute_line_total: number;
  }>;
  charged_total: number;
  refunded_cash: number;
  collected_cash: number;
  waived_amount: number;
}

export interface POSRefundReceipt {
  is_refund: boolean;
  store_name: string;
  store_address: string;
  store_phone: string;
  refund_number: string;
  original_transaction_number: string;
  date: string;
  time: string;
  cashier: string;
  items: Array<{
    name: string;
    quantity: number;
    unit: string;
    unit_price: number;
    total: number;
    tax: number;
  }>;
  subtotal: number;
  tax: number;
  surcharge: number;
  total: number;
  payment_method: string;
  card_type: string;
  card_last_four: string;
  reason: string;
}

export interface PettyCashWithdrawal {
  id: number;
  session_id: number;
  amount: number;
  category: string;
  category_display: string;
  description: string;
  withdrawn_by: string;
  created_at: string;
  time?: string;
}

export interface TillCloseReport {
  session: {
    id: number;
    cashier: string;
    terminal_id: string;
    opened_at: string;
    closed_at: string | null;
  };
  sales_summary: {
    transaction_count: number;
    subtotal: number;
    tax: number;
    discounts: number;
    surcharges: number;
    total: number;
    voided_count: number;
    refund_count: number;
    refund_total: number;
  };
  payment_breakdown: {
    cash: number;
    credit: number;
    debit: number;
    check: number;
    account?: number;
  };
  tab_paydowns?: number;
  cash_reconciliation: {
    opening: number;
    cash_sales: number;
    change_given: number;
    cash_refunds: number;
    petty_cash_out: number;
    expected: number;
    closing: number | null;
    variance: number | null;
  };
  petty_cash_withdrawals: Array<{
    time: string;
    category: string;
    description: string;
    withdrawn_by: string;
    amount: number;
  }>;
  tax_summary: {
    taxable_sales: number;
    exempt_sales: number;
    tax_collected: number;
  };
}

export const posAPI = {
  // Register Management
  getRegisters: (): Promise<{ registers: POSRegisterInfo[] }> => apiCall('/pos/registers/'),

  // Session Management
  openSession: (openingCash: number, openingCheckFloat: number = 0, terminalId: string = 'POS-1', registerId?: number) =>
    apiCall('/pos/sessions/open/', {
      method: 'POST',
      body: JSON.stringify({
        opening_cash: openingCash,
        opening_check_float: openingCheckFloat,
        terminal_id: terminalId,
        ...(registerId && { register_id: registerId }),
      }),
    }),

  closeSession: (sessionId: number, closingCash: number, closingCheckCount?: number | null, notes: string = '') =>
    apiCall(`/pos/sessions/${sessionId}/close/`, {
      method: 'POST',
      body: JSON.stringify({
        closing_cash: closingCash,
        ...(closingCheckCount != null && { closing_check_count: closingCheckCount }),
        notes,
      }),
    }),

  getCurrentSession: () => apiCall('/pos/sessions/current/'),

  // Product Lookup
  searchProducts: (query: string = '', categoryId?: number, quickButtonsOnly: boolean = false) => {
    const params = new URLSearchParams();
    if (query) params.append('q', query);
    if (categoryId) params.append('category', categoryId.toString());
    if (quickButtonsOnly) params.append('quick_buttons', 'true');
    return apiCall(`/pos/products/?${params.toString()}`);
  },

  lookupBarcode: (barcode: string) => apiCall(`/pos/products/barcode/${barcode}/`),

  getCategories: () => apiCall('/pos/categories/'),

  getQuickButtons: () => apiCall('/pos/quick-buttons/'),

  // Get products for browsing (by category, no search required)
  getProducts: (categoryId?: number | null, limit: number = 100) => {
    const params = new URLSearchParams();
    if (categoryId) params.append('category', categoryId.toString());
    params.append('limit', limit.toString());
    return apiCall(`/pos/products/?${params.toString()}`);
  },

  // Transaction Management
  createTransaction: () => apiCall('/pos/transactions/', { method: 'POST' }),

  // Resume an in-progress transaction by its human-readable number.
  // Used when StorePOS lands on /store/pos?txn=YYYYMMDD-NNNN after a
  // "Load in POS" action (BC order or wholesale Will Call order) so
  // the cashier sees the pre-staged cart instead of a fresh one.
  getTransactionByNumber: (transactionNumber: string) =>
    apiCall(`/pos/transactions/by-number/${transactionNumber}/`),

  // Parked carts (per-cashier persistent multi-cart).
  parkCart: (transactionId: number) =>
    apiCall(`/pos/transactions/${transactionId}/park/`, { method: 'POST' }),
  getParkedCarts: () => apiCall('/pos/parked-carts/'),
  recallCart: (transactionId: number) =>
    apiCall(`/pos/transactions/${transactionId}/recall/`, { method: 'POST' }),
  discardParkedCart: (transactionId: number) =>
    apiCall(`/pos/transactions/${transactionId}/discard/`, { method: 'DELETE' }),

  // Load a wholesale Will Call SalesOrder into a fresh POSTransaction.
  // Returns { transaction_id, transaction_number, redirect_url, ... }.
  // Sibling of the BC store_load_in_pos endpoint.
  loadWholesaleOrderInPos: (orderId: number) =>
    apiCall(`/pos/wholesale-orders/${orderId}/load-in-pos/`, { method: 'POST' }),

  // Invoice collection at the till (A1). List a customer's open
  // invoices (BC member resolves via member.customer; unlinked members
  // get { linked: false }), then apply/remove a single invoice-payment
  // line on the in-progress transaction. Amount is the COLLECTION
  // amount (<= balance_due) — never the order total.
  getOpenInvoices: (params: { member_id?: number; customer_id?: number }) => {
    const qs = params.member_id
      ? `member_id=${params.member_id}`
      : `customer_id=${params.customer_id}`;
    return apiCall(`/pos/open-invoices/?${qs}`);
  },

  applyInvoicePayment: (transactionId: number, invoiceId: number, amount: number, note: string = '') =>
    apiCall(`/pos/transactions/${transactionId}/invoice-payment/`, {
      method: 'POST',
      body: JSON.stringify({ invoice_id: invoiceId, amount, note }),
    }),

  removeInvoicePayment: (transactionId: number) =>
    apiCall(`/pos/transactions/${transactionId}/invoice-payment/`, { method: 'DELETE' }),

  // Customer tab (Phase 4): add/remove a pay-down-tab line, and charge to tab.
  addTabPayment: (transactionId: number, amount: number) =>
    apiCall(`/pos/transactions/${transactionId}/tab-payment/`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }),

  removeTabPayment: (transactionId: number) =>
    apiCall(`/pos/transactions/${transactionId}/tab-payment/`, { method: 'DELETE' }),

  // Charge (part of) the cart to the customer's tab. On an over-limit 409 the
  // caller can retry with managerPin to authorize. amount omitted = remaining.
  processTabCharge: (transactionId: number, amount?: number, managerPin?: string, allowOverride?: boolean) =>
    apiCall(`/pos/transactions/${transactionId}/pay/tab-charge/`, {
      method: 'POST',
      body: JSON.stringify({ amount, manager_pin: managerPin, allow_override: allowOverride }),
    }),

  addItem: (transactionId: number, productId: number, quantity: number = 1, isWeightBased: boolean = false, bulkBinId?: number, unitPrice?: number, allowOverride?: boolean, soldWeightLbs?: number) =>
    apiCall(`/pos/transactions/${transactionId}/items/`, {
      method: 'POST',
      body: JSON.stringify({
        product_id: productId,
        quantity,
        is_weight_based: isWeightBased,
        bulk_bin_id: bulkBinId,
        ...(unitPrice !== undefined && { unit_price: unitPrice }),
        ...(allowOverride ? { allow_override: true } : {}),
        // weight_tracked: the exact tag weight the cashier typed (fixed price).
        ...(soldWeightLbs !== undefined && { sold_weight_lbs: soldWeightLbs }),
      }),
    }),

  updateItem: (transactionId: number, itemId: number, quantity: number) =>
    apiCall(`/pos/transactions/${transactionId}/items/${itemId}/`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    }),

  removeItem: (transactionId: number, itemId: number) =>
    apiCall(`/pos/transactions/${transactionId}/items/${itemId}/delete/`, { method: 'DELETE' }),

  // Discounts
  applyItemDiscount: (transactionId: number, itemId: number, discountType: string, discountValue: number, reason: string = '') =>
    apiCall(`/pos/transactions/${transactionId}/discount/item/${itemId}/`, {
      method: 'POST',
      body: JSON.stringify({ discount_type: discountType, discount_value: discountValue, reason }),
    }),

  removeItemDiscount: (transactionId: number, itemId: number) =>
    apiCall(`/pos/transactions/${transactionId}/discount/item/${itemId}/remove/`, { method: 'DELETE' }),

  // Bag-credit: decline the auto-pull on a line and ring it as a normal sale.
  declineBagPull: (transactionId: number, itemId: number) =>
    apiCall(`/pos/transactions/${transactionId}/items/${itemId}/decline-bag-pull/`, { method: 'POST' }),

  applyCartDiscount: (transactionId: number, discountType: string, discountValue: number, reason: string = '') =>
    apiCall(`/pos/transactions/${transactionId}/discount/`, {
      method: 'POST',
      body: JSON.stringify({ discount_type: discountType, discount_value: discountValue, reason }),
    }),

  removeCartDiscount: (transactionId: number) =>
    apiCall(`/pos/transactions/${transactionId}/discount/remove/`, { method: 'DELETE' }),

  // Tax Exempt
  toggleTaxExempt: (transactionId: number) =>
    apiCall(`/pos/transactions/${transactionId}/tax-exempt/`, { method: 'POST' }),

  // Per-line cashier exemption control (Build B). Decoupled from product
  // eligibility — the cashier decides. In-progress transactions only.
  setLineTaxExempt: (transactionId: number, itemId: number, isTaxExempt: boolean, note?: string) =>
    apiCall(`/pos/transactions/${transactionId}/items/${itemId}/tax-exempt/`, {
      method: 'POST',
      body: JSON.stringify({
        is_tax_exempt: isTaxExempt,
        ...(note ? { exemption_note: note } : {}),
      }),
    }),

  voidTransaction: (transactionId: number, reason: string = '') =>
    apiCall(`/pos/transactions/${transactionId}/void/`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // Payment Processing — `amount` is optional. If supplied, applies that
  // slice of amount_due (split-payment); if omitted, behavior is unchanged
  // and the tender covers the whole remaining balance.
  processCashPayment: (transactionId: number, amountTendered: number, amount?: number, allowOverride?: boolean) =>
    apiCall(`/pos/transactions/${transactionId}/pay/cash/`, {
      method: 'POST',
      body: JSON.stringify({
        amount_tendered: amountTendered,
        ...(amount !== undefined ? { amount } : {}),
        ...(allowOverride ? { allow_override: true } : {}),
      }),
    }),

  initiateCardPayment: (transactionId: number, amount?: number, allowOverride?: boolean) =>
    apiCall(`/pos/transactions/${transactionId}/pay/card/`, {
      method: 'POST',
      body: JSON.stringify({
        ...(amount !== undefined ? { amount } : {}),
        ...(allowOverride ? { allow_override: true } : {}),
      }),
    }),

  // Register-side inventory corrections (the "Charles" set).
  // Inline count-in of uncounted stock (offered only on a code='oversell' block).
  quickAddInventory: (productId: number, quantity: number, notes?: string) =>
    apiCall(`/pos/inventory/quick-add/`, {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, quantity, ...(notes ? { notes } : {}) }),
    }),

  // Bag feed straight from a bulk bin into bagged floor inventory (one action).
  bagFromBin: (binId: number, storeProductId: number, numBags: number, allowOverride?: boolean) =>
    apiCall(`/pos/inventory/bag-from-bin/`, {
      method: 'POST',
      body: JSON.stringify({
        bin_id: binId, store_product_id: storeProductId, num_bags: numBags,
        ...(allowOverride ? { allow_override: true } : {}),
      }),
    }),

  checkCardPayment: (paymentId: number) =>
    apiCall(`/pos/payments/${paymentId}/status/`),

  cancelCardPayment: (paymentId: number) =>
    apiCall(`/pos/payments/${paymentId}/cancel/`, { method: 'POST' }),

  getTransactionAmountDue: (transactionId: number) =>
    apiCall(`/pos/transactions/${transactionId}/amount-due/`),

  // Purchase-history drawer payload. Pass either member_id (Bridge Club) or
  // visitor_id (UnmatchedVisitor shadow profile) — never both. The shape
  // returned is identical for both flavours so the drawer renders without
  // branching.
  getPurchaseHistory: (params: { memberId?: number | null; visitorId?: number | null }) => {
    const sp = new URLSearchParams();
    if (params.memberId) sp.append('member_id', String(params.memberId));
    if (params.visitorId) sp.append('visitor_id', String(params.visitorId));
    return apiCall(`/pos/customers/purchase-history/?${sp.toString()}`);
  },

  // Receipt
  getReceiptData: (transactionId: number): Promise<POSReceipt> =>
    apiCall(`/pos/transactions/${transactionId}/receipt/`),

  // Customer Lookup
  searchCustomers: (query: string) =>
    apiCall(`/pos/customers/?q=${encodeURIComponent(query)}`),

  // Create a new Bridge Club member from the POS (walk-in enrollment
  // at the register). On 201 success → { member: POSCustomer, ... }.
  // On 409 duplicate_email → existing_member is the POSCustomer-shape
  // payload so the caller can attach the existing member directly.
  // On 200 warning=phone_match → candidates[] for "Use this member"
  // affordance. ignore_phone_match=true bypasses the soft check.
  createBridgeClubMember: (params: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    attach_to_transaction_id?: number | null;
    ignore_phone_match?: boolean;
  }) =>
    apiCall('/pos/customers/create-bridge-club-member/', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  getCustomerDetail: (memberId: number) =>
    apiCall(`/pos/customers/${memberId}/`),

  lookupMemberByCode: (code: string) =>
    apiCall(`/pos/customers/lookup/${encodeURIComponent(code)}/`),

  attachCustomer: (transactionId: number, memberId: number | null) =>
    apiCall(`/pos/transactions/${transactionId}/customer/`, {
      method: 'POST',
      body: JSON.stringify({ member_id: memberId }),
    }),

  // Records that the cashier dismissed the BC signup prompt on an
  // unattributed walk-in (analytics only — no attribution/payment change).
  logBcSignupSkip: (transactionId: number) =>
    apiCall(`/pos/transactions/${transactionId}/log-bc-skip/`, {
      method: 'POST',
    }),

  // Promote an UnmatchedVisitor (walk-in shadow profile) into a real
  // BridgeClubMember by sending them a verification email.
  inviteToBridgeClub: (data: { unmatched_visitor_id: number; email: string }) =>
    apiCall(`/pos/customers/invite-to-bridge-club/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Link an UnmatchedVisitor to an EXISTING BridgeClubMember instead of
  // creating a new account — for walk-ins whose card belongs to someone
  // already in Bridge Club (e.g. a spouse). Credits the walk-in's
  // retroactive points to the chosen member. Returns 409 if already linked.
  linkWalkinToMember: (visitorId: number, memberId: number) =>
    apiCall(`/pos/walkins/${visitorId}/link-to-member/`, {
      method: 'POST',
      body: JSON.stringify({ member_id: memberId }),
    }),

  // Redeem 500 local points → $10 store credit. If transaction_id is
  // passed the credit is also stamped onto the POSTransaction.
  redeemLocalPoints: (memberId: number, points: number, transactionId?: number) =>
    apiCall(`/pos/customers/${memberId}/redeem-local-points/`, {
      method: 'POST',
      body: JSON.stringify({ points, transaction_id: transactionId ?? null }),
    }),

  // Bin Loading
  loadBin: (memberId: number, binId: number, data: {
    product_id?: number;
    quantity_lbs: number;
    pos_transaction_id?: number;
    product_name?: string;
  }) =>
    apiCall(`/pos/customers/${memberId}/bins/${binId}/load/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Session Summary & EOD Reports
  getSessionSummary: (sessionId: number) =>
    apiCall(`/pos/sessions/${sessionId}/summary/`),

  generateEODReport: (date?: string, regenerate: boolean = false) =>
    apiCall('/pos/reports/eod/', {
      method: 'POST',
      body: JSON.stringify({ date, regenerate }),
    }),

  getEODReport: (date: string) =>
    apiCall(`/pos/reports/eod/${date}/`),

  // Refunds
  lookupForRefund: (transactionNumber: string) =>
    apiCall('/pos/refunds/lookup/', {
      method: 'POST',
      body: JSON.stringify({ transaction_number: transactionNumber }),
    }),

  processRefund: (data: {
    transaction_id: number;
    refund_type: 'full' | 'partial';
    reason: string;
    items?: Array<{ item_id: number; quantity?: number }>;
    custom_amount?: number;
  }) =>
    apiCall('/pos/refunds/process/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getRefundReceiptData: (refundId: number): Promise<POSRefundReceipt> =>
    apiCall(`/pos/refunds/${refundId}/receipt/`),

  // Poll a pending card-present debit refund (customer taps at the terminal).
  getRefundStatus: (refundId: number): Promise<any> =>
    apiCall(`/pos/refunds/${refundId}/status/`),

  // Check Payment — `amount` is optional (split-payment). When omitted,
  // the check covers the entire remaining balance (existing behavior).
  processCheckPayment: (transactionId: number, checkNumber: string, amount?: number, allowOverride?: boolean) =>
    apiCall(`/pos/transactions/${transactionId}/pay/check/`, {
      method: 'POST',
      body: JSON.stringify({
        check_number: checkNumber,
        ...(amount !== undefined ? { amount } : {}),
        ...(allowOverride ? { allow_override: true } : {}),
      }),
    }),

  // Daily Transactions Report
  getDailyTransactions: (params: { date_from?: string; date_to?: string; cashier?: string }) => {
    const searchParams = new URLSearchParams();
    if (params.date_from) searchParams.append('date_from', params.date_from);
    if (params.date_to) searchParams.append('date_to', params.date_to);
    if (params.cashier) searchParams.append('cashier', params.cashier);
    return apiCall(`/pos/reports/daily/?${searchParams.toString()}`);
  },

  exportDailyTransactionsCSV: async (params: { date_from?: string; date_to?: string; cashier?: string }) => {
    const searchParams = new URLSearchParams();
    if (params.date_from) searchParams.append('date_from', params.date_from);
    if (params.date_to) searchParams.append('date_to', params.date_to);
    if (params.cashier) searchParams.append('cashier', params.cashier);
    searchParams.append('export', 'csv');
    const response = await fetch(`${API_BASE_URL}/pos/reports/daily/?${searchParams.toString()}`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to export CSV');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pos_transactions_${params.date_from || 'today'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Monthly Tax Report
  getMonthlyTaxReport: (params: { month?: string; date_from?: string; date_to?: string }) => {
    const searchParams = new URLSearchParams();
    if (params.month) searchParams.append('month', params.month);
    if (params.date_from) searchParams.append('date_from', params.date_from);
    if (params.date_to) searchParams.append('date_to', params.date_to);
    return apiCall(`/pos/reports/tax/?${searchParams.toString()}`);
  },

  exportMonthlyTaxCSV: async (params: { month?: string; date_from?: string; date_to?: string }) => {
    const searchParams = new URLSearchParams();
    if (params.month) searchParams.append('month', params.month);
    if (params.date_from) searchParams.append('date_from', params.date_from);
    if (params.date_to) searchParams.append('date_to', params.date_to);
    searchParams.append('export', 'csv');
    const response = await fetch(`${API_BASE_URL}/pos/reports/tax/?${searchParams.toString()}`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to export tax report CSV');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tax_report_${params.month || 'custom'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Petty Cash
  createPettyCashWithdrawal: (sessionId: number, amount: number, category: string, description: string = '') =>
    apiCall('/pos/petty-cash/', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, amount, category, description }),
    }),

  getPettyCashWithdrawals: (params?: { session_id?: number; date_from?: string; date_to?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.session_id) searchParams.append('session_id', params.session_id.toString());
    if (params?.date_from) searchParams.append('date_from', params.date_from);
    if (params?.date_to) searchParams.append('date_to', params.date_to);
    return apiCall(`/pos/petty-cash/?${searchParams.toString()}`);
  },

  // Till Close Report
  getCloseReport: (sessionId: number): Promise<TillCloseReport> =>
    apiCall(`/pos/sessions/${sessionId}/close-report/`),
};
